import React, { useEffect } from 'react';
import { useWishlist } from './WishlistContext';

const formatNaira = (amount) => `₦${Number(amount || 0).toLocaleString()}`;

const CartDrawer = ({
  open,
  onClose,
  orders = [],
  activeOrderIndex = 0,
  currentVendor,
  updateQuantity,
  onCheckout,
  getImageUrl,
  onOpenProduct,
  initialTab = 'cart',
}) => {
  const wishlist = useWishlist();
  const [tab, setTab] = React.useState(initialTab);

  // Reset tab when opening
  useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);

  // Close on ESC
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (open) window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Prevent body scroll when drawer open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  const items = orders[activeOrderIndex]?.items || [];
  const total = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className="fixed inset-0 bg-black/50 z-[100] transition-opacity"
        data-testid="cart-drawer-backdrop"
      />

      {/* Drawer */}
      <aside
        className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-white z-[101] shadow-2xl flex flex-col animate-slide-in-right"
        style={{ animation: 'slideInRight 0.25s ease-out' }}
        data-testid="cart-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <i className="fa-solid fa-bag-shopping text-cyan-500 text-lg" />
            <h2 className="text-lg font-bold text-gray-900">
              {tab === 'cart' ? `Cart (${itemCount})` : `Wishlist (${wishlist.count})`}
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="cart-drawer-close"
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setTab('cart')}
            data-testid="cart-tab-cart"
            className={`flex-1 py-3 text-sm font-semibold transition-colors ${
              tab === 'cart'
                ? 'text-cyan-700 border-b-2 border-cyan-500'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Cart {itemCount > 0 && <span className="ml-1 text-xs">({itemCount})</span>}
          </button>
          <button
            onClick={() => setTab('wishlist')}
            data-testid="cart-tab-wishlist"
            className={`flex-1 py-3 text-sm font-semibold transition-colors ${
              tab === 'wishlist'
                ? 'text-cyan-700 border-b-2 border-cyan-500'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Wishlist {wishlist.count > 0 && <span className="ml-1 text-xs">({wishlist.count})</span>}
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {tab === 'cart' ? (
            items.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {currentVendor && (
                  <div className="px-5 py-2.5 bg-cyan-50 text-xs text-cyan-900 font-semibold">
                    <i className="fa-solid fa-store mr-1" /> {currentVendor.name}
                  </div>
                )}
                {items.map((item) => (
                  <div key={item.product_id} className="flex gap-3 p-4">
                    <div className="w-16 h-16 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                      <img
                        src={getImageUrl ? getImageUrl(item.image_url) : item.image_url}
                        alt={item.product_name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = 'https://via.placeholder.com/80?text=Item';
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 line-clamp-2">
                        {item.product_name}
                      </p>
                      <p className="text-sm font-bold text-red-600 mt-1">
                        {formatNaira(item.price)}
                      </p>
                      <div className="mt-2 flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.product_id, -1)}
                          data-testid={`cart-decrement-${item.product_id}`}
                          className="w-7 h-7 rounded border border-gray-300 hover:bg-gray-100 text-gray-700 flex items-center justify-center"
                        >
                          <i className="fa-solid fa-minus text-xs" />
                        </button>
                        <span className="text-sm font-semibold w-6 text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.product_id, 1)}
                          data-testid={`cart-increment-${item.product_id}`}
                          className="w-7 h-7 rounded border border-gray-300 hover:bg-gray-100 text-gray-700 flex items-center justify-center"
                        >
                          <i className="fa-solid fa-plus text-xs" />
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => updateQuantity(item.product_id, -item.quantity)}
                      data-testid={`cart-remove-${item.product_id}`}
                      className="text-gray-400 hover:text-red-500 self-start"
                      aria-label="Remove"
                    >
                      <i className="fa-solid fa-trash text-sm" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center px-6 py-12">
                <div className="w-20 h-20 rounded-full bg-cyan-50 flex items-center justify-center mb-4">
                  <i className="fa-solid fa-bag-shopping text-3xl text-cyan-500" />
                </div>
                <h3 className="font-bold text-gray-900 mb-1">Your cart is empty</h3>
                <p className="text-sm text-gray-500 mb-5">
                  Browse a vendor and add products to see them here.
                </p>
                <button
                  onClick={onClose}
                  data-testid="cart-continue-shopping"
                  className="bg-cyan-500 hover:bg-cyan-700 text-white px-5 py-2.5 rounded-md text-sm font-semibold"
                >
                  Continue Shopping
                </button>
              </div>
            )
          ) : wishlist.items.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {wishlist.items.map((it) => (
                <div key={it.id} className="flex gap-3 p-4">
                  <button
                    type="button"
                    onClick={() => onOpenProduct?.(it)}
                    className="w-16 h-16 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    aria-label={`Open ${it.name}`}
                    data-testid={`wishlist-open-${it.id}`}
                  >
                    <img
                      src={getImageUrl ? getImageUrl(it.image_url || it.saved_image_url) : (it.image_url || it.saved_image_url)}
                      alt={it.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = 'https://via.placeholder.com/80?text=Item';
                      }}
                    />
                  </button>
                  <div className="flex-1 min-w-0">
                    <button type="button" onClick={() => onOpenProduct?.(it)} className="w-full text-left">
                      <p className="text-sm font-semibold text-gray-900 line-clamp-2 hover:text-cyan-700">{it.name}</p>
                    </button>
                    {it.vendor_name && <p className="text-[11px] text-gray-500 mt-0.5">{it.vendor_name}</p>}
                    <p className="text-sm font-bold text-red-600 mt-1">{formatNaira(it.price)}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Saved price - re-checks the current product when opened</p>
                  </div>
                  <button
                    onClick={() => wishlist.removeFromWishlist(it.id)}
                    data-testid={`wishlist-remove-${it.id}`}
                    className="w-8 h-8 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 self-start flex items-center justify-center"
                    aria-label={`Remove ${it.name} from wishlist`}
                  >
                    <i className="fa-solid fa-trash text-sm" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center px-6 py-12">
              <div className="w-20 h-20 rounded-full bg-cyan-50 flex items-center justify-center mb-4">
                <i className="fa-regular fa-heart text-3xl text-cyan-500" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1">Your wishlist is empty</h3>
              <p className="text-sm text-gray-500 mb-5">
                Tap the heart icon on any product to save it here.
              </p>
              <button
                onClick={onClose}
                className="bg-cyan-500 hover:bg-cyan-700 text-white px-5 py-2.5 rounded-md text-sm font-semibold"
              >
                Discover Products
              </button>
            </div>
          )}
        </div>

        {/* Footer / Checkout */}
        {tab === 'cart' && items.length > 0 && (
          <div className="border-t border-gray-200 px-5 py-4 space-y-3 bg-white">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Subtotal</span>
              <span className="font-bold text-gray-900">{formatNaira(total)}</span>
            </div>
            <button
              onClick={() => {
                onClose();
                onCheckout && onCheckout();
              }}
              data-testid="cart-checkout-btn"
              className="w-full bg-cyan-500 hover:bg-cyan-700 text-white font-bold py-3 rounded-md transition-colors flex items-center justify-center gap-2"
            >
              Proceed to Checkout <i className="fa-solid fa-arrow-right text-xs" />
            </button>
          </div>
        )}
      </aside>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
};

export default CartDrawer;
